/**
 * Pricing page — public, /pricing.
 * Per-active-client pricing matching afyanex.com.
 *  Starter $180 flat (up to 10) · Growth $17/client (11-30, featured) · Scale $15/client (31-75).
 */
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Check, ArrowRight, ShieldCheck } from "lucide-react";
import { track } from "@/lib/sentry";

const TIERS = [
  {
    key: "starter",
    name: "Starter",
    tag: "Up to 10 active clients",
    price: "$180",
    unit: "/mo flat",
    desc: "Moving off paper or Excel.",
    cta: "Start with Starter",
    to: "/agency/signup?plan=starter",
    features: [
      "AI clinical notes & voice dictation",
      "Geo clock-in (EVV-ready)",
      "MAR & vitals",
      "Family portal",
      "Bulk PDF audit exports",
      "Offline mobile · BAA included",
      "Email & in-app support",
    ],
  },
  {
    key: "growth",
    name: "Growth",
    tag: "11–30 active clients",
    price: "$17",
    unit: "/active client/mo",
    desc: "The small-agency sweet spot.",
    badge: "Most popular",
    cta: "Try Growth free 14 days",
    to: "/agency/signup?plan=growth",
    featured: true,
    features: [
      "Everything in Starter, plus:",
      "Drag-and-drop scheduling",
      "Open-shift broadcast",
      "Timecards & payroll CSV (Gusto, QBO, ADP)",
      "Stripe invoicing",
      "Applicant tracking system",
      "White-label family portal",
    ],
  },
  {
    key: "scale",
    name: "Scale",
    tag: "31–75 active clients",
    price: "$15",
    unit: "/active client/mo",
    desc: "Growing agencies, volume rate.",
    cta: "Talk to us",
    to: "mailto:hello@afyanex.com",
    contact: true,
    features: [
      "Everything in Growth, plus:",
      "Priority chat & phone support",
      "Migration assistance",
      "Custom note templates",
      "Dedicated onboarding manager",
      "Annual billing (save 2 months)",
    ],
  },
];

const FAQ = [
  { q: "What counts as an 'active client'?", a: "A client with at least one logged visit in a calendar month. If they have zero visits, they're not billed for that month — even if they remain in your roster." },
  { q: "Does Afyanex meet U.S. EVV requirements?", a: "Yes. Geo check-in/check-out is built in, with CSV exports to Sandata and HHAeXchange. Auto-SFTP submission is on the roadmap." },
  { q: "Is the family portal really free?", a: "Yes — families never pay and never count toward billing. Only active clients are billed." },
  { q: "Can I switch plans?", a: "Anytime. We pro-rate within the same billing cycle and let you upgrade or downgrade with one click." },
  { q: "Do you offer annual billing?", a: "Yes. Annual plans are billed up-front at the equivalent of 2 months free." },
];

function Wordmark() {
  return (
    <span className="afyanex-wordmark font-heading font-bold tracking-tight text-[19px]">
      Afya<span className="nex">nex</span>
    </span>
  );
}

export default function PricingPage() {
  useEffect(() => {
    track("pricing_page_viewed", { path: "/pricing" });
  }, []);
  return (
    <div className="min-h-screen bg-clinical-bg" data-testid="pricing-page">
      {/* Top nav */}
      <header className="sticky top-0 z-40 bg-clinical-bg/85 backdrop-blur-md border-b border-clinical-border/40">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="pricing-logo"><Wordmark /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-clinical-text">
            <Link to="/" className="hover:text-afyanex-blue transition">Home</Link>
            <Link to="/pricing" className="text-afyanex-blue">Pricing</Link>
            <Link to="/privacy" className="hover:text-afyanex-blue transition">Privacy</Link>
          </nav>
          <Link to="/agency/signup"
                className="inline-flex items-center gap-2 rounded-full bg-afyanex-blue px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_24px_-12px_rgba(0,94,255,0.6)] hover:-translate-y-px transition-all"
                data-testid="pricing-cta-nav">
            Try live demo <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute -top-20 right-1/4 h-[360px] w-[360px] rounded-full bg-afyanex-pink/15 blur-[120px]" />
        <div aria-hidden className="pointer-events-none absolute -top-10 left-1/4 h-[340px] w-[340px] rounded-full bg-afyanex-cyan/15 blur-[120px]" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8 pt-20 pb-12 text-center">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-afyanex-pink">Transparent pricing</div>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-clinical-text leading-[1.05]">
            Priced per <span className="bg-gradient-to-r from-afyanex-blue to-afyanex-pink bg-clip-text text-transparent">active client</span> — not per caregiver.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-clinical-subtext max-w-2xl mx-auto leading-relaxed">
            Caregivers fluctuate. Your client roster doesn't. Only active clients
            (one+ visit that month) count. 14-day free trial — no credit card.
          </p>
        </div>
      </section>

      {/* Tiers */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 pb-16">
        <div className="grid lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {TIERS.map((t) => (
            <div key={t.key}
                 data-testid={`pricing-tier-${t.key}`}
                 className={`relative rounded-2xl p-7 flex flex-col ${
                   t.featured
                     ? "bg-white border-2 border-afyanex-pink shadow-[0_30px_80px_-30px_rgba(255,70,163,0.4)]"
                     : "bg-white border border-clinical-border/70"
                 }`}>
              {t.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-afyanex-pink text-white text-[10px] uppercase tracking-[0.18em] font-semibold">
                  {t.badge}
                </div>
              )}
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-clinical-subtext">{t.tag}</div>
              <h3 className="mt-2 font-heading text-2xl font-semibold text-clinical-text">{t.name}</h3>
              <p className="mt-1 text-[14px] text-clinical-subtext">{t.desc}</p>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-heading text-4xl font-semibold text-clinical-text">{t.price}</span>
                <span className="text-sm text-clinical-subtext">{t.unit}</span>
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-[14px]">
                    <Check className="h-4 w-4 text-afyanex-blue mt-0.5 shrink-0" />
                    <span className={f.startsWith("Everything") ? "font-medium text-clinical-text" : "text-clinical-text"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to={t.to}
                    onClick={() => track("pricing_tier_clicked", { source: "pricing_page", tier: t.key, destination: t.to })}
                    className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-all ${
                      t.featured
                        ? "bg-afyanex-pink text-white shadow-[0_10px_28px_-12px_rgba(255,70,163,0.55)] hover:-translate-y-px"
                        : "border border-afyanex-blue/30 text-afyanex-blue hover:bg-afyanex-blue hover:text-white"
                    }`}
                    data-testid={`pricing-cta-${t.key}`}>
                {t.cta} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-xs text-clinical-subtext max-w-2xl mx-auto">
          All plans include unlimited admins, caregivers and family members ·
          email & in-app support · no setup fees · month-to-month or annual.
        </p>
      </section>

      {/* Trust */}
      <section className="mx-auto max-w-3xl px-5 sm:px-8 pb-16">
        <div className="rounded-2xl bg-white border border-clinical-border/70 p-6 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-afyanex-blue shrink-0 mt-0.5" />
          <div className="text-sm text-clinical-text leading-relaxed">
            <strong>HIPAA-aligned out of the box.</strong>{" "}
            <span className="text-clinical-subtext">
              Every plan includes a Business Associate Agreement signed in-app,
              AES-256 encryption at rest, TLS 1.3 in transit, tamper-evident
              audit logs, and brute-force protection. Your patient data stays
              compliant from day one.
            </span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 sm:px-8 pb-24">
        <div className="text-center mb-8">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-afyanex-pink">FAQ</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-clinical-text">
            Questions, answered.
          </h2>
        </div>
        <div className="rounded-2xl bg-white border border-clinical-border/70 px-6">
          {FAQ.map((f, i) => (
            <div key={i}
                 data-testid={`pricing-faq-${i}`}
                 className="border-b border-clinical-border/60 last:border-0 py-5">
              <div className="font-heading font-medium text-clinical-text">{f.q}</div>
              <p className="mt-1.5 text-[14.5px] text-clinical-subtext leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-clinical-border bg-clinical-bg">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Wordmark />
            <span className="text-xs text-clinical-subtext ml-2">© {new Date().getFullYear()} Afyanex Care, Inc.</span>
          </div>
          <nav className="flex items-center gap-5 text-xs text-clinical-subtext">
            <Link to="/privacy" className="hover:text-clinical-text">Privacy</Link>
            <Link to="/terms" className="hover:text-clinical-text">Terms</Link>
            <a href="mailto:hello@afyanex.com" className="hover:text-clinical-text">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
